import { Link } from "react-router";
import { motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { ProjectModal } from "./ProjectModal";
import { FloatingBenefitCard } from "./FloatingBenefitCard";
import { SineWaveBackground } from "./SineWaveBackground";
import { HeroSubtleElements } from "./HeroSubtleElements";

export function Hero() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section className="relative min-h-screen flex items-center pt-28 md:pt-32 pb-16 overflow-hidden bg-slate-50">
      <div className="absolute inset-0 z-0 opacity-10 pointer-events-none">
        <div className="absolute -top-[10%] -right-[10%] w-[50%] h-[50%] bg-[#FD5750] rounded-full blur-[120px]" />
        <div className="absolute top-[40%] -left-[10%] w-[30%] h-[30%] bg-blue-400 rounded-full blur-[100px]" />
      </div>

      <SineWaveBackground />
      <HeroSubtleElements />

      <div className="container mx-auto px-6 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-orange-100 text-[#FD5750] text-sm font-bold tracking-wide mb-6">
              IT Services & Strategic Consulting
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-slate-900">
              Transforming Vision into{" "}
              <span className="text-[#FD5750]">
                Digital Reality
              </span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-slate-600 leading-relaxed max-w-xl"
          >
            We combine the technical excellence of top-tier IT
            services with the strategic insight of premium
            consultancy. Building scalable, secure, and
            user-centric digital products for our clients.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-8 py-4 bg-[#FD5750] text-white font-semibold rounded-lg hover:bg-[#E04F49] transition-all shadow-lg shadow-[#FD5750]/25 flex items-center justify-center gap-2 group cursor-pointer"
            >
              Start Your Project
              <ArrowRight
                className="group-hover:translate-x-1 transition-transform"
                size={20}
              />
            </button>
            <Link
              to="/services"
              className="px-8 py-4 bg-white text-slate-700 font-semibold rounded-lg border border-slate-200 hover:border-[#FD5750] hover:text-[#FD5750] transition-all flex items-center justify-center"
            >
              Explore Services
            </Link>
          </motion.div>

          <div className="pt-8 flex items-center gap-8 text-slate-500 text-sm font-medium">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span>Trusted by 20+ Clients</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              <span>150+ Projects Delivered</span>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative hidden md:block mt-8 lg:mt-0"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-200/50 aspect-video lg:aspect-[4/3]">
            <img
              src="https://images.unsplash.com/photo-1527697911963-20cb424e9608?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080"
              alt="Digital Product Strategy and Design"
              className="object-cover w-full h-full"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 to-transparent"></div>
          </div>

          <FloatingBenefitCard />
        </motion.div>
      </div>

      <ProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
}